import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import type { Document } from "@contentful/rich-text-types";
import {
  attributeToDisplay,
  flattenAttributes,
  isVariantAttribute,
  isVariantAttributesContainer,
  normalizeAttributeForComparison,
  type AttributeDisplay,
} from "./attribute-display";
import type { ImagePreview } from "./display-types";

export type FlatVariant = Record<string, unknown>;

export type VariantColumn = {
  id: string;
  label: string;
  sku: string;
  key: string;
  flat: FlatVariant;
};

export type { AttributeDisplay, ImagePreview };

export type MediaGalleryItem =
  | { kind: "image"; image: ImagePreview }
  | { kind: "video"; label: string; posterUrl?: string };

export type CellDisplay =
  | { kind: "empty" }
  | { kind: "text"; text: string }
  | { kind: "rich-text"; text: string }
  | { kind: "image"; image: ImagePreview }
  | { kind: "media-gallery"; items: Array<MediaGalleryItem> }
  | { kind: "attribute"; attribute: AttributeDisplay };

export type ComparisonRow = {
  path: string;
  cells: Array<CellDisplay>;
  differs: boolean;
  cellKind: CellDisplay["kind"];
};

export function normalizePdpPath(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) {
    return "";
  }

  try {
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      const url = new URL(trimmed);
      const byPathIndex = url.pathname.indexOf("/storefront/pages/by-path");
      if (byPathIndex >= 0) {
        return url.pathname.slice(byPathIndex + "/storefront/pages/by-path".length) || "/";
      }
      return url.pathname;
    }
  } catch {
    // fall through
  }

  if (trimmed.startsWith("/storefront/pages/by-path")) {
    return trimmed.slice("/storefront/pages/by-path".length) || "/";
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRichTextDocument(value: unknown): value is Document {
  return isPlainObject(value) && value.nodeType === "document" && Array.isArray(value.content);
}

function isExpandedRichText(value: unknown): value is { type: "expanded"; json: Document } {
  return isPlainObject(value) && value.type === "expanded" && isRichTextDocument(value.json);
}

function isRichTextValue(value: unknown): boolean {
  return isExpandedRichText(value) || isRichTextDocument(value);
}

export function normalizeAssetUrl(url: string): string {
  return url.startsWith("//") ? `https:${url}` : url;
}

function isImageUrlString(value: string): boolean {
  return value.startsWith("//images.") || /^https?:\/\/.+\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(value);
}

function isAsset(value: unknown): value is {
  url: string;
  title?: string;
  width?: number;
  height?: number;
  contentType?: string;
  __typename?: string;
} {
  return isPlainObject(value) && typeof value.url === "string" && value.url.length > 0;
}

function isImageWithFocalPoint(
  value: unknown,
): value is { __typename: "UtilityImageWithFocalPoint"; image: Record<string, unknown> } {
  return isPlainObject(value) && value.__typename === "UtilityImageWithFocalPoint" && isAsset(value.image);
}

function isUtilityVideo(value: unknown): value is {
  __typename: "UtilityVideo";
  video?: { secure_url?: string; url?: string; poster?: { url?: string } };
} {
  return isPlainObject(value) && value.__typename === "UtilityVideo";
}

export function assetFromValue(value: unknown): ImagePreview | undefined {
  if (isAsset(value)) {
    return {
      url: normalizeAssetUrl(value.url),
      title: typeof value.title === "string" ? value.title : undefined,
      width: typeof value.width === "number" ? value.width : undefined,
      height: typeof value.height === "number" ? value.height : undefined,
    };
  }

  if (isImageWithFocalPoint(value)) {
    return assetFromValue(value.image);
  }

  return undefined;
}

function mediaGalleryItemFromValue(value: unknown): MediaGalleryItem | undefined {
  const image = assetFromValue(value);
  if (image) {
    return { kind: "image", image };
  }

  if (isUtilityVideo(value)) {
    const posterUrl = value.video?.poster?.url ? normalizeAssetUrl(value.video.poster.url) : undefined;
    return {
      kind: "video",
      label: "Video",
      posterUrl,
    };
  }

  return undefined;
}

function isMediaValue(value: unknown): boolean {
  return mediaGalleryItemFromValue(value) !== undefined;
}

function isMediaGallery(value: unknown): value is Array<unknown> {
  return Array.isArray(value) && value.length > 0 && value.every(item => item === null || isMediaValue(item));
}

export function richTextToPlainText(value: unknown): string | undefined {
  if (isExpandedRichText(value)) {
    return documentToPlainTextString(value.json).trim();
  }

  if (isRichTextDocument(value)) {
    return documentToPlainTextString(value).trim();
  }

  return undefined;
}

function truncateText(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

function normalizeCellValue(value: unknown): string {
  if (value === undefined) {
    return "";
  }
  if (value === null) {
    return "null";
  }

  if (isVariantAttribute(value)) {
    return normalizeAttributeForComparison(value);
  }

  const richText = richTextToPlainText(value);
  if (richText !== undefined) {
    return richText;
  }

  const image = assetFromValue(value);
  if (image) {
    return image.url;
  }

  if (isMediaGallery(value)) {
    return value
      .map(item => {
        const mediaItem = item === null ? undefined : mediaGalleryItemFromValue(item);
        if (!mediaItem) {
          return "";
        }
        if (mediaItem.kind === "image") {
          return mediaItem.image.url;
        }
        return mediaItem.posterUrl ?? mediaItem.label;
      })
      .join("|");
  }

  if (typeof value === "string") {
    return isImageUrlString(value) ? normalizeAssetUrl(value) : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return JSON.stringify(value, null, 0);
}

export function buildCellDisplay(value: unknown): CellDisplay {
  if (value === undefined) {
    return { kind: "empty" };
  }
  if (value === null) {
    return { kind: "text", text: "null" };
  }

  if (isVariantAttribute(value)) {
    return { kind: "attribute", attribute: attributeToDisplay(value) };
  }

  const richText = richTextToPlainText(value);
  if (richText !== undefined) {
    return { kind: "rich-text", text: richText.length > 0 ? truncateText(richText, 500) : "—" };
  }

  const image = assetFromValue(value);
  if (image) {
    return { kind: "image", image };
  }

  if (isMediaGallery(value)) {
    const items = value.flatMap(item => {
      if (item === null) {
        return [];
      }
      const mediaItem = mediaGalleryItemFromValue(item);
      return mediaItem ? [mediaItem] : [];
    });

    if (items.length > 0) {
      return { kind: "media-gallery", items };
    }
  }

  if (typeof value === "string") {
    if (isImageUrlString(value)) {
      return { kind: "image", image: { url: normalizeAssetUrl(value) } };
    }

    return { kind: "text", text: truncateText(value, 120) };
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return { kind: "text", text: String(value) };
  }

  const json = JSON.stringify(value, null, 0);
  return { kind: "text", text: truncateText(json, 160) };
}

export function flattenVariant(variant: Record<string, unknown>, prefix = ""): FlatVariant {
  const result: FlatVariant = {};

  for (const [key, value] of Object.entries(variant)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (value === null || value === undefined || typeof value !== "object") {
      result[path] = value;
      continue;
    }

    if (isRichTextValue(value) || isMediaValue(value) || isVariantAttribute(value)) {
      result[path] = value;
      continue;
    }

    if (Array.isArray(value)) {
      result[path] = isMediaGallery(value) ? value : value;
      continue;
    }

    if (isPlainObject(value)) {
      if (path === "attributes" && isVariantAttributesContainer(value)) {
        Object.assign(result, flattenAttributes(value, path));
        continue;
      }

      Object.assign(result, flattenVariant(value, path));
      continue;
    }

    result[path] = value;
  }

  return result;
}

export function buildComparisonRows(variants: Array<VariantColumn>): Array<ComparisonRow> {
  const paths = new Set<string>();
  for (const variant of variants) {
    for (const path of Object.keys(variant.flat)) {
      paths.add(path);
    }
  }

  return [...paths]
    .sort((a, b) => a.localeCompare(b))
    .map(path => {
      const rawValues = variants.map(variant => variant.flat[path]);
      const normalizedValues = rawValues.map(normalizeCellValue);
      const differs = new Set(normalizedValues).size > 1;
      const cells = rawValues.map(buildCellDisplay);
      const cellKind = cells.find(cell => cell.kind !== "empty")?.kind ?? "empty";

      return {
        path,
        cells,
        differs,
        cellKind,
      };
    });
}

export function variantLabel(variant: Record<string, unknown>): { sku: string; key: string; name: string } {
  return {
    sku: typeof variant.sku === "string" ? variant.sku : "?",
    key: typeof variant.key === "string" ? variant.key : "?",
    name: typeof variant.name === "string" ? variant.name : "?",
  };
}

export function variantColumnsFromList(variants: Array<Record<string, unknown>>): Array<VariantColumn> {
  return variants.map(variant => {
    const { sku, key, name } = variantLabel(variant);

    return {
      id: sku,
      sku,
      key,
      label: name,
      flat: flattenVariant(variant),
    };
  });
}
